import { LayoutParams, LayoutResult, TileStyle, PipShape, PipPosition } from './types';
import {
  BORDER_RADIUS_8,
  NO_RADIUS,
  TARGET_TILE_AR,
  SCREENSHARE_TILE_AR_4_3,
  GRID_DISTRIBUTION_PORTRAIT,
  GRID_DISTRIBUTION_PORTRAIT_WITH_SCREEN,
  PORTRAIT_SHARED_FILL_TILE_AR
} from './constant';
import { tile, incompleteRow, buildSpeakerGrid, getContainedScreenRect, pipRectPosition } from './utils';

/**
 * Row distribution for FILL / TILES layouts.
 * Key = speaker count, Value = array of "speakers per row" (top to bottom).
 *
 * Pattern: 1→[1], 2→[2], 3→[2,1], 4→[2,2], 5→[2,3], 6→[3,3], 7→[3,4], 8→[4,4]
 */

/**
 * Row distribution for SHARED_FILL speaker panel.
 * Each row entry: [colsInRow]. A row marked with `fw: true` means a
 * single speaker spans the full panel width (rendered as 1 column).
 *
 * Pattern:
 *   1 → 1 speaker (full-width)
 *   2 → 2 rows × 1 col
 *   3 → 1 full-width + 1 row × 2 cols
 *   4 → 2 rows × 2 cols
 *   5 → 1 full-width + 2 rows × 2 cols
 *   6 → 3 rows × 2 cols
 *   7 → 1 full-width + 3 rows × 2 cols
 *   8 → 4 rows × 2 cols
 */

// ═══════════════════════════════════════════════════════════════════════════
// Layout computers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * FILL – edge-to-edge, no gaps, no border radius.
 *
 * Row heights are derived from tile width × 16:9 AR so every row is
 * proportionally correct.  The grid is scaled to exactly fill the
 * container height.  Incomplete rows use the max-cols tile width so
 * their tiles are the same size as their neighbours, then centred.
 */
function computeFill(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.02 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  const distTable = GRID_DISTRIBUTION_PORTRAIT;
  const dist = distTable[Math.min(count, 24)] || distTable[24];

  const { lastIdx, isIncomplete: lastIsIncomplete, tileW: incompleteTileW } = incompleteRow(dist, gap, usableW);

  // Natural (AR-preserving) row height = tileWidth / 16:9
  const naturalRowH = dist.map((cols, i) => {
    const tw = i === lastIdx && lastIsIncomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    return tw / TARGET_TILE_AR;
  });

  const rowCount = dist.length;
  const vGaps = (rowCount - 1) * gap;
  // Scale so all rows together fill exactly usableH
  const scale = (usableH - vGaps) / naturalRowH.reduce((a, b) => a + b, 0);

  const tiles: TileStyle[] = [];
  let topOffset = margin;

  for (let r = 0; r < dist.length; r++) {
    const cols = dist[r];
    const incomplete = r === lastIdx && lastIsIncomplete;
    const cellW = incomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    const cellH = naturalRowH[r] * scale;
    const rowW = cols * cellW + (cols - 1) * gap;
    const leftStart = incomplete ? margin + (usableW - rowW) / 2 : margin;

    for (let c = 0; c < cols; c++) {
      tiles.push(tile(topOffset, leftStart + c * (cellW + gap), cellW, cellH, W, H, radius));
    }
    topOffset += cellH + gap;
  }

  return tiles;
}

/**
 * TILES – all speakers with gaps and rounded corners.
 *
 * Each row's height is derived from its tile width and TARGET_TILE_AR so
 * every tile shares the same display aspect ratio.  When the AR-based grid
 * is taller than the container the heights are scaled down proportionally
 * (tiles get slightly flatter but stay uniform).  When shorter, the grid
 * is centred vertically.
 */
function computeTiles(W: number, H: number, count: number, showBorders: boolean, roundedCorners: boolean): TileStyle[] {
  const margin = showBorders ? W * 0.004 : 0;
  const gap = showBorders ? W * 0.01 : 0;
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const distTable = GRID_DISTRIBUTION_PORTRAIT;
  const dist = distTable[Math.min(count, 24)] || distTable[24];
  // TILES always maintains 16:9 aspect ratio (scale capped at 1)
  const scaleCap = 1;

  return buildSpeakerGrid(
    dist,
    gap,
    W - 2 * margin, // usableW
    H - 2 * margin, // containerH
    margin, // baseY
    margin, // left margin
    W,
    H,
    radius,
    scaleCap
  );
}

/** Minimum share of the stage height the screen keeps so it never collapses. */
const PORTRAIT_MIN_SCREEN_FRACTION = 0.25;

/**
 * Maximum share of the stage height the speaker grid may take. The screen share
 * is the primary content, so the speakers are capped below the full height — this
 * stops a single (or few) speaker from filling the stage and guarantees some
 * top/bottom padding around the centred screen + speakers stack.
 */
const PORTRAIT_MAX_SPEAKER_FRACTION = 0.5;

/**
 * Natural (un-scaled) height a portrait speaker grid needs so its tiles render
 * at their real aspect ratio instead of being squashed. Mirrors the sizing in
 * `buildSpeakerGrid`.
 */
function portraitSpeakerGridHeight(dist: number[], gap: number, usableW: number): number {
  const { lastIdx, isIncomplete, tileW: incompleteTileW } = incompleteRow(dist, gap, usableW);
  const rowsHeight = dist.reduce((sum, cols, i) => {
    const tw = i === lastIdx && isIncomplete ? incompleteTileW : (usableW - (cols - 1) * gap) / cols;
    return sum + tw / PORTRAIT_SHARED_FILL_TILE_AR;
  }, 0);
  return rowsHeight + (dist.length - 1) * gap;
}

/**
 * Portrait screen-share layout (used by SHARED_FILL and FILM_STRIP when H ≥ W).
 *
 * On portrait containers (mobile phones, narrow windows) a left/right split
 * produces an unusably narrow speaker panel. Instead the stage stacks the screen
 * on top and the speaker cards below.
 *
 * The split is adaptive rather than a fixed 70 / 30: the screen stays at 16:9 but
 * the speaker region is given as much height as the grid needs to render at its
 * natural tile ratio, bounded between a minimum screen height and a maximum
 * speaker share. So as the speaker count grows (a taller natural grid) or the
 * stage gets shorter, the speakers reclaim height from the screen up to their
 * cap, while a single/few speakers stay bounded so the primary screen share keeps
 * its space. The leftover height becomes outer top/bottom padding around the
 * centred screen + speakers stack, keeping the two adjacent.
 *
 * Both SHARED_FILL and FILM_STRIP render identically on portrait — the
 * left/right "side" setting is irrelevant and ignored.
 */
function computePortraitScreenShare(
  W: number,
  H: number,
  count: number,
  showBorders: boolean,
  roundedCorners: boolean
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;

  const margin = showBorders ? W * 0.004 : 0;
  const sectionGap = showBorders ? W * 0.01 : 0;

  const tileGap = showBorders ? sectionGap : 0;
  const usableW = W - 2 * margin;
  const usableH = H - 2 * margin;

  // Screen's natural height at a full-width 16:9 ratio.
  const idealScreenH = usableW / TARGET_TILE_AR;

  // No speakers: the screen alone, kept at 16:9 and vertically centred.
  if (count === 0) {
    const screenH = Math.min(idealScreenH, usableH);
    const screenTop = margin + Math.max(0, (usableH - screenH) / 2);
    return [tile(screenTop, margin, usableW, screenH, W, H, radius)];
  }

  const dist =
    GRID_DISTRIBUTION_PORTRAIT_WITH_SCREEN[Math.min(count, 24)] || GRID_DISTRIBUTION_PORTRAIT_WITH_SCREEN[24];

  // Hand the speakers the height their grid needs, but never starve the screen
  // below its minimum share and never let them exceed their maximum share. More
  // speakers / a shorter stage therefore give the speakers more room (up to the
  // cap) rather than flattening the tiles, while a single speaker can't take the
  // whole stage from the primary screen share.
  const naturalGridH = portraitSpeakerGridHeight(dist, tileGap, usableW);
  const minScreenH = Math.min(idealScreenH, usableH * PORTRAIT_MIN_SCREEN_FRACTION);
  // The screen share is primary, so the speaker grid never grows taller than the
  // screen itself (and never beyond a hard fraction of the stage on short stages).
  // This keeps the screen the largest element and leaves clear top/bottom padding.
  const maxSpeakerAreaH = Math.min(idealScreenH, usableH * PORTRAIT_MAX_SPEAKER_FRACTION);
  const speakerAreaH = Math.max(0, Math.min(naturalGridH, maxSpeakerAreaH, usableH - minScreenH - sectionGap));
  const screenH = Math.max(minScreenH, Math.min(idealScreenH, usableH - speakerAreaH - sectionGap));

  // Centre the screen + speaker stack so spare height becomes outer padding
  // rather than a gap between the screen and the speakers.
  const stackH = screenH + sectionGap + speakerAreaH;
  const stackTop = margin + Math.max(0, (usableH - stackH) / 2);
  const screenTop = stackTop;
  const speakerAreaTop = screenTop + screenH + sectionGap;

  const tiles: TileStyle[] = [tile(screenTop, margin, usableW, screenH, W, H, radius)];

  if (speakerAreaH <= 0) {
    return tiles;
  }

  // Keep the existing portrait row pattern, but compute speaker heights from a
  // portrait-specific tile aspect ratio so remainder rows do not appear taller
  // than the first row.
  return [
    ...tiles,
    ...buildSpeakerGrid(
      dist,
      tileGap,
      usableW,
      speakerAreaH,
      speakerAreaTop,
      margin,
      W,
      H,
      radius,
      1,
      'top',
      PORTRAIT_SHARED_FILL_TILE_AR
    )
  ];
}

/**
 * PIP – full-screen screen share with a small speaker overlay.
 * The PiP position is controlled separately via PIP_POSITION_STYLES.
 */
function computePip(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  pipShape: PipShape,
  pipPosition: PipPosition = 'RIGHT_BOTTOM',
  keepAspectRatio: boolean = false,
  maintainScreenAspectRatio: boolean = true
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;
  const { screenTop, screenLeft, screenW, screenH } = maintainScreenAspectRatio
    ? getContainedScreenRect(W, H, margin)
    : { screenTop: margin, screenLeft: margin, screenW: W - 2 * margin, screenH: H - 2 * margin };

  const tiles: TileStyle[] = [];

  // Element 0: Screen share fitted into a contained 16:9 box (or full-bleed — see above).
  tiles.push(tile(screenTop, screenLeft, screenW, screenH, W, H, radius));

  // Element 1: PiP speaker overlay anchored to the selected corner
  if (pipShape === 'CIRCULAR') {
    // Uses explicit top/left (via pipRectPosition + tile()) rather than
    // auto/bottom/right/transform so the editor's selection hit-box
    // (which only reads top/left/width/height) can always find it.
    const sizeFraction = W >= H ? 0.14 : 0.38;
    const pipW = W * sizeFraction;
    const pipH = pipW;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, screenTop, screenLeft, screenW, screenH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, '100%', { zIndex: 5 }));
  } else {
    // keepAspectRatio: 16:9 to match webcam feeds; otherwise 4:3.
    const pipAr = keepAspectRatio ? TARGET_TILE_AR : SCREENSHARE_TILE_AR_4_3;
    const sizeFraction = W >= H ? 0.18 : 0.48;
    const pipW = screenW * sizeFraction;
    const pipH = pipW / pipAr;
    const { pipTop, pipLeft } = pipRectPosition(pipPosition, pipW, pipH, screenTop, screenLeft, screenW, screenH, W, H);
    tiles.push(tile(pipTop, pipLeft, pipW, pipH, W, H, radius, { zIndex: 5 }));
  }

  return tiles;
}

/**
 * ONLY_SCREEN – screen share takes the entire stage.
 *
 * By default the screen is letterboxed to a contained 16:9 box so 16:9 media
 * (slides, screen-shares) is shown whole on tall portrait containers. When
 * `maintainScreenAspectRatio` is false the screen fills the entire usable area
 * instead — used for full-bleed content such as a broadcast overlay.
 */
function computeOnlyScreen(
  W: number,
  H: number,
  _count: number,
  showBorders: boolean,
  roundedCorners: boolean,
  maintainScreenAspectRatio: boolean
): TileStyle[] {
  const radius = roundedCorners ? BORDER_RADIUS_8 : NO_RADIUS;
  const margin = showBorders ? W * 0.004 : 0;

  if (!maintainScreenAspectRatio) {
    const usableW = W - 2 * margin;
    const usableH = H - 2 * margin;
    return [tile(margin, margin, usableW, usableH, W, H, radius)];
  }

  const { screenTop, screenLeft, screenW, screenH } = getContainedScreenRect(W, H, margin);
  return [tile(screenTop, screenLeft, screenW, screenH, W, H, radius)];
}

// ═══════════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════════

export function computePortraitLayout(params: LayoutParams): LayoutResult {
  const {
    layout,
    aspectRatio,
    speakerCount,
    showBorders,
    roundedCorners,
    pipShape = 'RECTANGULAR',
    pipPosition = 'RIGHT_BOTTOM' as const,
    maintainScreenAspectRatio = true,
    keepAspectRatio = false
  } = params;

  if (aspectRatio <= 0) {
    return { tiles: [] };
  }

  // Normalised coordinate space: W is always 100, H scales with aspect ratio.
  // tile() emits cqw/cqh so the browser resolves them against the real container.
  const W = 100;
  const H = aspectRatio * 100;
  const count = Math.max(1, Math.min(speakerCount, 24));

  switch (layout) {
    case 'FILL':
      return { tiles: computeFill(W, H, count, showBorders, roundedCorners) };

    case 'TILES':
      return { tiles: computeTiles(W, H, count, showBorders, roundedCorners) };

    case 'SHARED_FILL':
      return { tiles: computePortraitScreenShare(W, H, count, showBorders, roundedCorners) };

    case 'FILM_STRIP':
      return { tiles: computePortraitScreenShare(W, H, count, showBorders, roundedCorners) };

    case 'PIP':
      return {
        tiles: computePip(
          W,
          H,
          count,
          showBorders,
          roundedCorners,
          pipShape,
          pipPosition,
          keepAspectRatio,
          maintainScreenAspectRatio
        )
      };

    case 'ONLY_SCREEN':
      return { tiles: computeOnlyScreen(W, H, count, showBorders, roundedCorners, maintainScreenAspectRatio) };

    default:
      return { tiles: [] };
  }
}
